import {
  PERMISSION_LABEL,
  ROLE_LABEL,
  VIEW_LABEL,
  type MenuViewKey,
  type Permission,
  type Role,
  type ViewKey,
} from './role';

/**
 * What a screen or a permission rests on. The lab's request of 11 October 2026:
 * roles, the access matrix and a person's own access are to be free of error,
 * to work, and to be customisable.
 *
 * Every box of the matrix takes effect, and so does every grant and denial on a
 * person's record: the audit of that day flipped each one against the API and
 * read the session after it. What it found is the other half. A screen and the
 * permission to do its work are two boxes, and nothing said so: Users given
 * without "Manage users & permissions" opened on an empty list for good,
 * Payments without "Mark paid" on four blank totals and "Nothing to settle",
 * and a receptionist given Final Check without "Pass / fail Final Check" on a
 * list with nothing to press. Each was a customisation that looked as if it had
 * failed.
 *
 * So the pairs are written down once, here, and read in three places: the
 * matrix, which lists what a role holds that will not work as given; a person's
 * Special access, which lists the same for them; and the screen itself, which
 * says what is missing instead of showing nothing. The matrix and Special
 * access say the reverse as well: a permission given without the one screen it
 * is used on (`PERMISSION_SCREENS`).
 *
 * Nothing here grants or refuses anything. The server decides every call as it
 * did; this is only the explanation.
 */
export interface ScreenNeed {
  /** Any one of these lets the screen do its work. */
  anyOf: readonly Permission[];
  /** What the screen is without one: closed, or there to read and not to change. */
  without: 'closed' | 'readOnly';
}

export const SCREEN_NEEDS: Partial<Record<MenuViewKey, ScreenNeed>> = {
  // Every call the screen makes asks for it; without it the list never arrives.
  users: { anyOf: ['admin'], without: 'closed' },
  // The lists are money, and so is every button.
  payments: { anyOf: ['pay'], without: 'closed' },
  // Reading a record is seeing a name.
  doctors: { anyOf: ['seeIdentity'], without: 'closed' },
  patients: { anyOf: ['seeIdentity'], without: 'closed' },
  // The screen is one form, and sending it is the permission.
  intake: { anyOf: ['intake'], without: 'closed' },
  // These are read by whoever holds them, and changed by the permission alone.
  settings: { anyOf: ['admin'], without: 'readOnly' },
  finalcheck: { anyOf: ['finalcheck'], without: 'readOnly' },
  shipping: { anyOf: ['ship'], without: 'readOnly' },
  items: { anyOf: ['pricing'], without: 'readOnly' },
  // Hold and Release are open to whoever takes payments or dispatches.
  onhold: { anyOf: ['pay', 'ship'], without: 'readOnly' },
};

/** A permission that does nothing without another: a record is changed by somebody who may read it. */
export const PERMISSION_NEEDS: Partial<Record<Permission, Permission>> = {
  manageClients: 'seeIdentity',
  renameClients: 'seeIdentity',
  mergeDoctors: 'seeIdentity',
};

/**
 * A permission that is used on one screen and nowhere else: given without that
 * screen, there is nowhere to use it. This is the lab's own case, the other way
 * round from `SCREEN_NEEDS`: a receptionist was given "Pass / fail Final Check"
 * and not the Final Check screen, where a case is passed, and had no way to
 * reach one but a notification. Only the three whose one screen is certain.
 */
export const PERMISSION_SCREENS: Partial<Record<Permission, MenuViewKey>> = {
  finalcheck: 'finalcheck',
  intake: 'intake',
  pricing: 'items',
};

/*
 * Nothing here about the two screens of performance figures. They were refused
 * to a technician and to CAD / CAM whatever was ticked, and this file said so
 * for a day. The lab's answer of 11 October 2026 was that the tick always
 * wins: the server asks only for the screen now, and so there is nothing to
 * explain.
 */

/**
 * The roles that work no department. Taking a case over brings it to the
 * taker's own department, and they have none to bring it to.
 */
export const NO_DEPARTMENT_ROLES: readonly Role[] = ['reception', 'accountant'];

const quoted = (permission: Permission) => `"${PERMISSION_LABEL[permission]}"`;

/** `"A"`, or `"A" or "B"`. */
function anyOfThem(permissions: readonly Permission[]): string {
  return permissions.map(quoted).join(' or ');
}

function needOf(view: ViewKey): ScreenNeed | null {
  return (SCREEN_NEEDS as Partial<Record<ViewKey, ScreenNeed>>)[view] ?? null;
}

function lacks(need: ScreenNeed, permissions: readonly Permission[]): boolean {
  return !need.anyOf.some((permission) => permissions.includes(permission));
}

/**
 * Why a screen this person holds shows them nothing, in words for them to read,
 * or null when it is open to them.
 */
export function screenClosedReason(view: ViewKey, permissions: readonly Permission[]): string | null {
  const need = needOf(view);
  if (need === null || need.without !== 'closed' || !lacks(need, permissions)) return null;
  return `This screen needs ${anyOfThem(need.anyOf)}, which your account does not have. Ask management to give it to you.`;
}

/**
 * Why a screen this person holds is theirs to read and not to change, in words
 * for them to read, or null when they may change it.
 */
export function screenReadOnlyReason(view: ViewKey, permissions: readonly Permission[]): string | null {
  const need = needOf(view);
  if (need === null || need.without !== 'readOnly' || !lacks(need, permissions)) return null;
  return `You can read this screen. Changing anything on it needs ${anyOfThem(need.anyOf)}, which your account does not have.`;
}

/**
 * Everything a role, or one person, holds that will not work as given: one
 * sentence each, for whoever is giving the access. Empty when all of it works.
 *
 * `views` and `permissions` are what is held in the end: a role's row of the
 * matrix, or a person's role with their own exceptions applied.
 */
export function accessGaps(
  role: Role,
  views: readonly ViewKey[],
  permissions: readonly Permission[],
): string[] {
  const gaps: string[] = [];

  for (const view of views) {
    const need = needOf(view);
    if (need === null || !lacks(need, permissions)) continue;
    gaps.push(
      need.without === 'closed'
        ? `${VIEW_LABEL[view]} shows nothing without ${anyOfThem(need.anyOf)}.`
        : `${VIEW_LABEL[view]} can be read, and changed only with ${anyOfThem(need.anyOf)}.`,
    );
  }

  for (const permission of permissions) {
    const needed = PERMISSION_NEEDS[permission];
    if (needed !== undefined && !permissions.includes(needed)) {
      gaps.push(`${quoted(permission)} does nothing without ${quoted(needed)}.`);
    }
    const screen = PERMISSION_SCREENS[permission];
    if (screen !== undefined && !views.includes(screen)) {
      gaps.push(`${quoted(permission)} has nowhere to be used without the ${VIEW_LABEL[screen]} screen.`);
    }
  }

  if (permissions.includes('takeOver') && NO_DEPARTMENT_ROLES.includes(role)) {
    gaps.push(
      `${quoted('takeOver')} does nothing for ${ROLE_LABEL[role]}, who work no department to take a case to.`,
    );
  }

  return gaps;
}
